import {
  captureGupLanding,
  clearGupSsoFailed,
  consumeGupSessionReset,
  getGupLanding,
  getPendingGupLanding,
  getPortalRedirect,
  isGupSsoFailed,
  isGupSsoStarted,
  isGupTheqaSkipped,
  markGupSsoFailed,
  markGupSsoStarted,
} from '@/lib/gup';
import { PageLoading } from '@/components/page-loading';
import { useRef } from 'react';
import { AuthForm } from '@/components/auth-form';
import { AuthService } from '@/services/authService';
import { useAuthStore } from '@/store/auth';
import { Button } from '@opaz/ui-components';
import { createFileRoute, redirect, useLocation, useNavigate } from '@tanstack/react-router';
import { ErrorResponse } from 'oidc-client-ts';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmailAuth } from './-components/EmailAuth';
import { LoginStepper } from './-components/LoginStepper';
import { OtpAuth } from './-components/OtpAuth';

type BeforeInstallPromptEvent = Event & {
  prompt: () => void;
};

export const Route = createFileRoute('/login/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      redirect: search.redirect as string | undefined,
    };
  },
  beforeLoad: ({ search }) => {
    // Back from the Keycloak logout of a new Gov.om entry: continue with the Gov.om link it stored.
    if (!search.redirect && consumeGupSessionReset()) {
      const landing = getPendingGupLanding();
      if (landing) throw redirect({ to: '/login', search: { redirect: landing }, replace: true });
    }
    captureGupLanding(search.redirect);
    if (AuthService.isTokenExpired()) {
      console.debug('[/login]: User is not authenticated.');
    } else {
      console.debug('[/login]: User is authenticated. Redirecting...');
      throw redirect({ to: getPortalRedirect(search.redirect) || '/dashboard' });
    }
  },
  head: () => {
    return {
      meta: [
        {
          title: 'Login - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { redirect: redirectUrl } = Route.useSearch();
  const location = useLocation();

  const [showOtp, setShowOtp] = useState(false);
  const [loginMethod, setLoginMethod] = useState<'email' | 'pki'>('email');
  const { authService } = useAuthStore((state) => state);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [userCredentials, setUserCredentials] = useState<{ username: string; password: string }>({
    username: '',
    password: '',
  });
  const gupRedirectStarted = useRef(false);
  const isGupLogin = !!getGupLanding(redirectUrl);
  const skipTheqa = isGupTheqaSkipped();
  const [gupLoginFailed, setGupLoginFailed] = useState(false);
  useEffect(() => {
    if (!isGupLogin) return;
    // Back from a THEQA page that never completed the round trip (also when restored from the back/forward cache).
    const fallBackToLogin = () => {
      if (!isGupSsoStarted()) return;
      markGupSsoFailed();
      setGupLoginFailed(true);
    };
    const onPageShow = (event: PageTransitionEvent) => event.persisted && fallBackToLogin();
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [isGupLogin]);
  useEffect(() => {
    if (!isGupLogin || skipTheqa || gupRedirectStarted.current) return;
    if (isGupSsoStarted()) {
      markGupSsoFailed();
      setGupLoginFailed(true);
      return;
    }
    if (isGupSsoFailed()) return;
    const store = useAuthStore.getState();
    if (!store.authService) store.setAuthService('email');
    captureGupLanding(redirectUrl);
    gupRedirectStarted.current = true;
    markGupSsoStarted();
    void useAuthStore
      .getState()
      .authService!.signinRedirect('theqa')
      .catch(() => {
        markGupSsoFailed();
        setGupLoginFailed(true);
      });
  }, [isGupLogin, redirectUrl, skipTheqa]);

  const [authError, setAuthError] = useState<ErrorResponse | undefined>(undefined);

  // TODO: Remove temporary solution for testing the new pki login
  if (new URLSearchParams(location.searchStr || window.location.search).get('pkiLogin') === 'v2') {
    localStorage.setItem('pkiLogin', 'v2');
  }

  const isPkiLoginV2 = useMemo(() => {
    //return localStorage.getItem('pkiLogin') === 'v2';
    return true;
  }, []);

  const handleAuthSuccess = () => {
    console.debug('[/login]: Login success.');
    setUserCredentials({ username: '', password: '' });
    const installPromptShown = localStorage.getItem('installPromptShown') == 'true';

    if (deferredPrompt && !installPromptShown) {
      localStorage.setItem('installPromptShown', 'true');
      deferredPrompt.prompt();
      setDeferredPrompt(null);
    }

    // Navigate to the redirect URL if available, otherwise to dashboard
    clearGupSsoFailed();
    if (redirectUrl) {
      navigate({ to: getPortalRedirect(redirectUrl) ?? '/dashboard' });
    } else {
      navigate({ to: '/dashboard' });
    }
  };

  const handleAuthError = (error: ErrorResponse) => {
    setAuthError(error);
    console.debug('[/login]: Login failed.');
  };

  // Called by EmailAuth after successful login
  const handleShowOtp = (email: string, password: string, isDirectLogin: boolean) => {
    if (isDirectLogin) {
      handleAuthSuccess();
      return;
    }

    setAuthError(undefined);
    setUserCredentials({ username: email, password });
    setShowOtp(true);
  };

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', (event) => {
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    });
  }, []);

  if (isGupLogin && !skipTheqa && !gupLoginFailed && !isGupSsoFailed()) return <PageLoading />;

  if (showOtp) {
    return (
      <OtpAuth
        userCredentials={userCredentials}
        handleAuthSuccess={handleAuthSuccess}
        redirectUrl={redirectUrl}
        setShowOtp={setShowOtp}
        setUserCredentials={setUserCredentials}
      />
    );
  }

  return (
    <AuthForm error={authError} onBack={undefined}>
      <LoginStepper currentStep={1} />

      <div className="mb-8">
        <h1 className="font-bold text-3xl text-[#1A444F] mb-6">{t('login.title')}</h1>
        <p className="text-xl text-muted-foreground">{t('login.description')}</p>
      </div>

      {isPkiLoginV2 && (
        <div className="flex gap-2 mb-8">
          <Button variant={loginMethod === 'email' ? 'default' : 'secondary'} onClick={() => setLoginMethod('email')}>
            {t('login.authMethods.email.label')}
          </Button>

          <Button variant={loginMethod === 'pki' ? 'default' : 'secondary'} onClick={() => setLoginMethod('pki')}>
            {t('login.authMethods.pki.label')}
          </Button>
        </div>
      )}

      {loginMethod === 'email' && (
        <EmailAuth onAuthSuccess={handleShowOtp} onAuthError={handleAuthError} redirectUrl={redirectUrl} />
      )}

      {loginMethod === 'pki' && (
        <>
          <Button
            type="button"
            className="w-full my-10"
            onClick={async () => {
              await authService?.signinRedirect('theqa', getPortalRedirect(redirectUrl));
            }}
          >
            {t('login.authMethods.pki.actionButtonLabel')}
          </Button>
        </>
      )}
    </AuthForm>
  );
}
